import React, { FC, useCallback, useMemo } from 'react'
import { Icon, Button } from '@wmgtech/legato'
import compact from 'lodash/fp/compact'

import { useCurrentUser } from 'modules/User/useCurrentUser'
import { useZoneMenu, useSpaces, useZonesList } from 'modulesCommon/serviceHooks'
import { IZoneNotifyProps } from './ZoneNotify.props'
import styles from './zone-notify.module.scss'

type TUserEnv = {
  type: 'ZONE'
  id: string
  name: string
  countryCode: string
} | {
  type: 'SPACE'
  id: string
  name: string
  countryCode: string
}

function labelAdapter(...labels: Array<string | undefined | null>): string {
  return compact(labels).join('/')
}

export const ZoneNotify: FC<IZoneNotifyProps> = ({ handleAppear }) => {

  const { user } = useCurrentUser()
  const { getSpaceById } = useSpaces()
  const { getZoneById } = useZonesList()
  const {
    currentZone,
    currentSpace,
    countryCode,
    onSelectLabel,
    onSelectSpace,
  } = useZoneMenu()

  const userEnv = useMemo<TUserEnv>(
    () => {
      if (typeof user?.label_id === 'string') {
        const zone = getZoneById(user.label_id)
        return {
          type: 'ZONE',
          id: user.label_id,
          name: zone?.name ?? '',
          countryCode: user?.country_code ?? ''
        }
      } else if (typeof user?.space_id === 'string') {
        const space = getSpaceById(user?.space_id)
        return {
          type: 'SPACE',
          id: user.space_id,
          name: space?.name ?? '',
          countryCode: user?.country_code ?? ''
        }
      }
      throw new Error('Invalid user')
    },
    [getSpaceById, getZoneById, user],
  )

  const currentEnv = useMemo<string>(
    () => {
      if (typeof currentZone !== 'undefined') {
        return labelAdapter(currentZone?.name, countryCode)
      } else if (typeof currentSpace !== 'undefined') {
        return labelAdapter(currentSpace.name, countryCode)
      }
      return ''
    },
    [countryCode, currentSpace, currentZone],
  )

  const isVisible = useMemo<boolean>(
    () => {
      if (
        typeof currentZone === 'undefined' &&
        typeof currentSpace === 'undefined'
      ) return false
      return currentZone?.name !== userEnv.name
    },
    [currentSpace, currentZone, userEnv.name],
  )

  const handleSetEnv = useCallback(
    () => {
      if (userEnv.type === 'ZONE') {
        onSelectLabel(userEnv.id)
      } else if (userEnv.type === 'SPACE') {
        onSelectSpace(userEnv.id, userEnv.countryCode)
      }
    },
    [onSelectLabel, onSelectSpace, userEnv],
  )

  return isVisible ? (
    <div className={styles.container} ref={handleAppear}>
      <div className={styles.inner}>
        <Icon name="triangle-exclamation" prefix="fas" />
        <p className={styles.message}>
          You are currently viewing {currentEnv}. Your home zone is
        </p>
        <Button
          onClick={handleSetEnv}
          containerStyle="link"
          htmlType="button"
          colorType="info"
        >{labelAdapter(userEnv.name, userEnv.countryCode)}</Button>
      </div>
    </div>
  ) : null
}
